import { Button, Callout } from '@dev-spendesk/grapes';
import React from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { Spinner } from 'src/core/common/components/Spinner/Spinner';
import { type VerificationOfPayeeResult } from 'src/core/modules/verification-of-payee/apis';

type Props = {
  result: VerificationOfPayeeResult;
  onIgnore: () => void;
  onUpdate: (name: string) => void;
  className?: string;
  isLoading?: boolean;
};

export const VerificationOfPayeeInconsistencyStatusCallout = ({
  result,
  onIgnore,
  onUpdate,
  className,
  isLoading,
}: Props) => {
  const { t } = useTranslation('global');

  if (isLoading) {
    return <Spinner />;
  }

  if (result.matchingResult === 'close_match') {
    const payeeSuggestedName = result.payeeSuggestedName;
    if (!payeeSuggestedName) {
      throw new Error('Payee suggested name should be defined here');
    }

    return (
      <Callout
        variant="warning"
        title={t('verificationOfPayee.callout.closeMatch.title', {
          name: payeeSuggestedName,
        })}
        className={className}
      >
        <p className="mb-16">
          {t('verificationOfPayee.callout.closeMatch.description')}
        </p>
        <div className="flex gap-8">
          <Button
            variant="primaryWarning"
            onClick={() => onUpdate(payeeSuggestedName)}
            text={t('verificationOfPayee.callout.closeMatch.update', {
              name: payeeSuggestedName,
            })}
          />
          <Button
            variant="secondaryNeutral"
            onClick={onIgnore}
            text={t('verificationOfPayee.callout.ignore')}
          />
        </div>
      </Callout>
    );
  }

  if (result.matchingResult === 'no_match') {
    return (
      <Callout
        variant="alert"
        title={t('verificationOfPayee.callout.noMatch.title')}
        className={className}
      >
        <p className="mb-16">
          {t('verificationOfPayee.callout.noMatch.description')}
        </p>
        <Button
          variant="primaryAlert"
          onClick={onIgnore}
          text={t('verificationOfPayee.callout.ignoreAndProceed')}
        />
      </Callout>
    );
  }

  if (result.matchingResult === 'impossible_match') {
    return (
      <Callout
        variant="alert"
        title={t('verificationOfPayee.callout.impossibleMatch.title')}
        className={className}
      >
        <p className="mb-16">
          {t('verificationOfPayee.callout.impossibleMatch.description')}
        </p>
        <Button
          variant="primaryAlert"
          onClick={onIgnore}
          text={t('verificationOfPayee.callout.ignoreAndProceed')}
        />
      </Callout>
    );
  }

  return null;
};
